import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const PUBLIC_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const MEMBER_LINKS = [
  { to: "/insights", label: "Insights" },
  { to: "/weekly-outlook", label: "Weekly Outlook" },
  { to: "/trade-ideas", label: "Trade Ideas" },
  { to: "/education", label: "Education" },
  { to: "/profile", label: "Profile" },
];

function NavItem({ link }) {
  const [hovered, setHovered] = useState(false);
  return (
    <NavLink
      to={link.to}
      end={link.to === "/"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => ({
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        fontFamily: "var(--font-body)",
        fontSize: "13px",
        letterSpacing: "0.06em",
        textDecoration: "none",
        borderRadius: "6px",
        transition: "all 0.25s",
        color: isActive ? "#f5c518" : hovered ? "#f0f0f0" : "#888",
        background: isActive
          ? "rgba(245,197,24,0.07)"
          : hovered
          ? "rgba(255,255,255,0.04)"
          : "transparent",
        border: isActive
          ? "1px solid rgba(245,197,24,0.22)"
          : hovered
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid transparent",
        boxShadow: isActive
          ? "0 0 16px rgba(245,197,24,0.12), inset 0 0 12px rgba(245,197,24,0.04)"
          : hovered
          ? "0 0 12px rgba(255,255,255,0.04)"
          : "none",
        textShadow: isActive ? "0 0 20px rgba(245,197,24,0.5)" : hovered ? "0 0 12px rgba(240,240,240,0.2)" : "none",
      })}
    >
      {({ isActive }) => (
        <>
          {/* Active dot */}
          {isActive && (
            <span style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "#f5c518",
              display: "inline-block",
              boxShadow: "0 0 8px #f5c518, 0 0 16px #f5c51860",
              animation: "navDotPulse 2s ease-in-out infinite",
              flexShrink: 0,
            }} />
          )}
          {link.label}
          {/* Bottom glow line on active */}
          {isActive && (
            <span style={{
              position: "absolute",
              bottom: -1,
              left: "20%",
              right: "20%",
              height: 1,
              background: "linear-gradient(90deg, transparent, #f5c518, transparent)",
              boxShadow: "0 0 8px #f5c51880",
            }} />
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { user, isAdmin, isApproved, logout } = useAuth();
  const navigate = useNavigate();

  // Which links to show
  const NAV_LINKS = (isAdmin || isApproved)
    ? MEMBER_LINKS
    : PUBLIC_LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    setTimeout(() => setLoaded(true), 100);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      <style>{`
        @keyframes navFadeDown {
          from { opacity: 0; transform: translateY(-14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes logoGlow {
          0%,100% { text-shadow: none; }
          50%      { text-shadow: 0 0 28px #c9a84c40, 0 0 56px #c9a84c18; }
        }
        @keyframes dotPulse {
          0%,100% { box-shadow: 0 0 0 0 #4ade8055; }
          60%      { box-shadow: 0 0 0 7px #4ade8000; }
        }
        @keyframes navDotPulse {
          0%,100% { box-shadow: 0 0 4px #f5c518, 0 0 8px #f5c51860; }
          50%      { box-shadow: 0 0 10px #f5c518, 0 0 24px #f5c51880; }
        }
        @keyframes menuSlide {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes linkFadeIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>

      <nav style={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        zIndex: 1000,
        background: scrolled ? "rgba(5,5,5,0.95)" : "transparent",
        borderBottom: scrolled ? "1px solid #1e1e1e" : "1px solid transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        transition: "all 0.4s ease",
        opacity: loaded ? 1 : 0,
        animation: loaded ? "navFadeDown 0.55s ease both" : "none",
      }}>

        {/* Scrolled bottom glow */}
        {scrolled && (
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
            background: "linear-gradient(90deg, transparent, #c9a84c28, #f5c51820, #c9a84c28, transparent)",
            pointerEvents: "none",
          }} />
        )}

        <div className="container" style={{ display: "flex", alignItems: "center", height: 68, justifyContent: "space-between" }}>

          {/* ── Logo ── */}
          <Link to="/" style={{ textDecoration: "none", display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{
              fontFamily: "var(--font-serif)",
              fontSize: "1.28rem",
              fontWeight: 600,
              color: "#f0f0f0",
              letterSpacing: "0.05em",
              animation: "logoGlow 4s ease-in-out infinite",
            }}>
              Macro Trader
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                width: 5, height: 5, borderRadius: "50%",
                background: "#4ade80",
                display: "inline-block",
                animation: "dotPulse 2.2s ease-in-out infinite",
              }} />
              <span style={{
                fontFamily: "var(--font-mono)",
                fontSize: "8px",
                letterSpacing: "0.24em",
                color: "#c9a84c",
                textTransform: "uppercase",
              }}>Research &amp; Analysis</span>
            </span>
          </Link>

          {/* ── Desktop Nav ── */}
          <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {NAV_LINKS.map((link) => (
              <NavItem key={link.to} link={link} />
            ))}

            <div style={{ display: "flex", gap: 8, marginLeft: 12 }}>
              {!user ? (
                <Link to="/login" style={{ padding: "7px 16px", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: "6px", color: "#c9a84c", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", transition: "all 0.25s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.15)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(201,168,76,0.2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(201,168,76,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                >Login</Link>
              ) : (
                <>
                  <Link to={isAdmin ? "/admin" : "/dashboard"} style={{ padding: "7px 16px", background: "rgba(245,197,24,0.08)", border: "1px solid rgba(245,197,24,0.25)", borderRadius: "6px", color: "#f5c518", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(245,197,24,0.15)"; e.currentTarget.style.boxShadow = "0 0 20px rgba(245,197,24,0.2)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(245,197,24,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                  >{isAdmin ? "Admin" : "Dashboard"}</Link>
                  <button onClick={handleLogout} style={{ padding: "7px 16px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: "6px", color: "#666", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#444"; e.currentTarget.style.color = "#aaa"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = "#666"; }}
                  >Logout</button>
                </>
              )}
            </div>
          </div>

          {/* ── Mobile Hamburger ── */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="mobile-menu-btn"
            style={{
              background: "none",
              border: "1px solid #2a2a2a",
              color: "#f0f0f0",
              width: 40, height: 40,
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "6px",
              flexDirection: "column",
              gap: 5,
              padding: "10px",
              transition: "border-color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#c9a84c40"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "#2a2a2a"}
          >
            {[0, 1, 2].map(j => (
              <span key={j} style={{
                display: "block", width: "100%", height: 1,
                background: "#c0c0c0",
                transition: "all 0.25s",
                transform: menuOpen ? (j === 0 ? "rotate(45deg) translate(4px,4px)" : j === 2 ? "rotate(-45deg) translate(4px,-4px)" : "none") : "none",
                opacity: menuOpen && j === 1 ? 0 : 1,
              }} />
            ))}
          </button>
        </div>

        {/* ── Mobile Menu ── */}
        {menuOpen && (
          <div style={{
            background: "#080808",
            borderTop: "1px solid #1e1e1e",
            padding: "16px 0 24px",
            animation: "menuSlide 0.25s ease both",
          }}>
            <div className="container">
              {NAV_LINKS.map((link, i) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "13px 0",
                    fontFamily: "var(--font-body)",
                    fontSize: "15px",
                    color: isActive ? "#f5c518" : "#777",
                    borderBottom: "1px solid #141414",
                    textDecoration: "none",
                    animation: "linkFadeIn 0.3s " + (i * 0.04) + "s both",
                    textShadow: isActive ? "0 0 16px rgba(245,197,24,0.4)" : "none",
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <span style={{
                        width: 4, height: 4, borderRadius: "50%",
                        background: isActive ? "#f5c518" : "#333",
                        boxShadow: isActive ? "0 0 8px #f5c518" : "none",
                        flexShrink: 0,
                      }} />
                      {link.label}
                    </>
                  )}
                </NavLink>
              ))}
              <div style={{ paddingTop: 16, display: "flex", gap: 10 }}>
                {!user ? (
                  <Link to="/login" onClick={() => setMenuOpen(false)} style={{ padding: "8px 18px", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: "6px", color: "#c9a84c", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none" }}>Login</Link>
                ) : (
                  <>
                    <Link to={isAdmin ? "/admin" : "/dashboard"} onClick={() => setMenuOpen(false)} style={{ padding: "8px 18px", background: "rgba(245,197,24,0.08)", border: "1px solid rgba(245,197,24,0.25)", borderRadius: "6px", color: "#f5c518", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none" }}>{isAdmin ? "Admin" : "Dashboard"}</Link>
                    <button onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: "6px", color: "#666", fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer" }}>Logout</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
